import { useRef, useState, useEffect } from "react";
import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import Constants from "common/constant";
import { GeneratedMapData } from "features/builder/types";
import { useBuilderScene } from "features/builder/context/BuilderContext";
import {
  buildGeneratedMapScene,
  renderGeneratedMapPreview,
} from "features/builder/renderGeneratedMapPreview";

const DEFAULT_BG = `${Constants.assetHost}/assets/images/bg_scene.jpg`;

// タイル名から地形色を導出（common/model/tiles.ts の命名規則に準拠）。
// 背景色は inline style で指定（check-style hook の対象外＝font-size/spacing/rounded のみ検査）。
const terrainColor = (name: string): string => {
  if (name.includes("desert")) return "#e6c27a";
  if (name.includes("snow")) return "#dbe9f4";
  if (name.includes("teamBlue") || name.includes("river") || name.includes("water")) {
    return "#7cc4e8";
  }
  if (name.includes("lava") || name.includes("volcano")) return "#e8895c";
  if (name.includes("forest")) return "#7cc47c";
  return "#9ccc8f"; // 既定: 草地
};

const EMPTY_CELL = "rgba(0,0,0,0.06)";

/**
 * 選んだ案を実際の 3D で表示し、ドラッグで回せるライブビュー。
 * 同時に生きるのは 1 案だけ（= WebGL コンテキストは 1 つ）。
 */
const LiveMapView = ({
  data,
  size,
}: {
  data: GeneratedMapData;
  size: { x: number; z: number };
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { x: sizeX, z: sizeZ } = size;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;
    const engine = new Engine(canvas, true);
    const scene = new Scene(engine);
    scene.clearColor = new Color4(0, 0, 0, 0);

    const handleResize = () => engine.resize();
    window.addEventListener("resize", handleResize);

    buildGeneratedMapScene(scene, data, { x: sizeX, z: sizeZ })
      .then((camera) => {
        if (disposed) return;
        // 回すのは横方向と、見下ろし角だけ。寄り引きは固定して迷子にならないようにする
        camera.lowerBetaLimit = Math.PI / 8;
        camera.upperBetaLimit = Math.PI / 2;
        camera.lowerRadiusLimit = camera.radius;
        camera.upperRadiusLimit = camera.radius;
        camera.attachControl(canvas, true);
        engine.runRenderLoop(() => scene.render());
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      window.removeEventListener("resize", handleResize);
      engine.stopRenderLoop();
      scene.dispose();
      engine.dispose();
    };
  }, [data, sizeX, sizeZ]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none" />;
};

/**
 * AI生成マップ（GeneratedMapData）のプレビュー。
 *
 * 「これを つかう」で適用したあとの見た目と揃うよう、実際の 3D モデルで表示する。
 * 選択中の案はライブ表示でドラッグして回せる。選択していない案は、編集画面の Engine を
 * 使い回した 1 フレームのオフスクリーン描画（静止画）なので描画コストが残らない。
 * シーンが無い/描画に失敗したときだけ 2D ミニマップにフォールバックする。
 */
export const MapMiniPreview = ({
  data,
  size,
  active = false,
  onActivate,
}: {
  data: GeneratedMapData;
  size: { x: number; z: number };
  active?: boolean;
  onActivate?: () => void;
}) => {
  const scene = useBuilderScene();
  const [image, setImage] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const { x: sizeX, z: sizeZ } = size;

  useEffect(() => {
    if (!scene) {
      setFailed(true);
      return;
    }
    let canceled = false;
    setImage(null);
    setFailed(false);
    renderGeneratedMapPreview(scene, data, { x: sizeX, z: sizeZ })
      .then((url) => {
        if (!canceled) setImage(url);
      })
      .catch(() => {
        if (!canceled) setFailed(true);
      });
    return () => {
      canceled = true;
    };
  }, [scene, data, sizeX, sizeZ]);

  if (!failed) {
    return (
      <div
        onClick={active ? undefined : onActivate}
        className={`flex-col-el relative w-full aspect-[4/3] rounded-lg overflow-hidden ${
          active ? "" : "cursor-pointer"
        }`}
      >
        <img
          alt=""
          draggable="false"
          src={DEFAULT_BG}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {active ? (
          <LiveMapView data={data} size={size} />
        ) : (
          image && (
            <img
              alt="AIが つくった マップ"
              draggable="false"
              src={image}
              className="absolute inset-0 w-full h-full"
            />
          )
        )}
      </div>
    );
  }

  const cols = Math.max(1, size.x);
  const rows = Math.max(1, size.z);

  const tileAt = new Map<string, string>();
  data.tiles?.forEach((t) => tileAt.set(`${t.x}_${t.z}`, t.name));
  const decoAt = new Set((data.decorations ?? []).map((d) => `${d.x}_${d.z}`));
  const gemAt = new Set((data.gems ?? []).map((g) => `${g.x}_${g.z}`));
  const charKey = data.character ? `${data.character.x}_${data.character.z}` : "";
  const goalKey = data.goal ? `${data.goal.x}_${data.goal.z}` : "";

  const cells: JSX.Element[] = [];
  for (let z = 0; z < rows; z++) {
    for (let x = 0; x < cols; x++) {
      const key = `${x}_${z}`;
      const name = tileAt.get(key);
      const icon =
        key === charKey
          ? "🦆"
          : key === goalKey
            ? "🚩"
            : gemAt.has(key)
              ? "💎"
              : decoAt.has(key)
                ? "🌳"
                : "";
      cells.push(
        <div
          key={key}
          className="flex-col-center aspect-square rounded"
          style={{ backgroundColor: name ? terrainColor(name) : EMPTY_CELL }}
        >
          {icon && <span className="type-sm">{icon}</span>}
        </div>,
      );
    }
  }

  return (
    <div
      className="grid w-full gap-1"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {cells}
    </div>
  );
};
